import { Component, inject, signal, OnInit, CUSTOM_ELEMENTS_SCHEMA, resource, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subject, debounceTime, distinctUntilChanged, lastValueFrom, tap } from 'rxjs';
import { DialogRef } from '../../../core/services/dialog.service';
import { PatientsService } from '../../../shared/services/patients.service';
import { StaffService } from '../../../shared/services/staff.service';
import { BusinessService } from '../../../shared/services/business.service';
import { BusinessContextService } from '../../../core/services/business-context.service';

@Component({
    selector: 'app-search-dialog',
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    templateUrl: './app-search-dialog.html',
    styleUrl: './app-search-dialog.css'
})
export class EntitySearchDialog implements OnInit {
    private dialogRef = inject(DialogRef);
    private patientsService = inject(PatientsService);
    private staffService = inject(StaffService);
    private businessService = inject(BusinessService);
    private bizCtx = inject(BusinessContextService);

    // Config properties
    type = input<'patient' | 'doctor' | 'business' | null>(null);
    displayFn!: (item: any) => string;
    placeholder: string = 'Buscar...';
    entityIcon: string = 'solar:user-bold-duotone';

    // Search state (Signals)
    page = signal(1);
    limit = signal(12);
    searchQ = signal('');
    results = signal<any[]>([]);
    total = signal(0);
    searchTerm = ''; // For NgModel binding

    private searchSubject = new Subject<string>();

    // ── Resource ────────────────────────────────────────────────────────────
    searchResource = resource({
        params: () => {
            const t = this.type();
            if (!t) return null; // Wait for type set in OnInit
            return {
                type: t,
                page: this.page(),
                limit: this.limit(),
                search: this.searchQ(),
                business: this.bizCtx.business()
            };
        },
        loader: async ({ params }) => {
            if (!params) return { data: [] };
            const { type, page, limit, search, business } = params;

            if (type === 'doctor' && !business) return { data: [] };

            let obs$: import('rxjs').Observable<any>;
            
            if (type === 'patient') {
                obs$ = this.patientsService.getAll({ page, limit, search, isActive: true });
            } else if (type === 'business') {
                obs$ = this.businessService.getAll({ page, limit, search });
            } else {
                obs$ = this.staffService.getAll({ page, limit, search, business });
            }

            return lastValueFrom(
                obs$.pipe(
                    tap((res: any) => {
                        const data = res.data || res;
                        if (this.page() === 1) {
                            this.results.set(data);
                        } else {
                            this.results.update(current => [...current, ...data]);
                        }
                        if (res.meta) {
                            this.total.set(res.meta.total || 0);
                        }
                    })
                )
            );
        }
    });

    isLoading = this.searchResource.isLoading;

    // Quick create state (Patients only)
    isCreating = false;
    saving = signal(false);

    idTypeOptions = [
        { label: 'Cédula', value: 'CEDULA' },
        { label: 'RUC', value: 'RUC' },
        { label: 'Pasaporte', value: 'PASAPORTE' }
    ];


    ngOnInit() {
        const t = this.type() || this.dialogRef.config?.data?.type;

        if (t === 'patient') {
            this.entityIcon = 'solar:user-id-bold-duotone';
            this.placeholder = 'Buscar paciente...';
            this.displayFn = (item) => `${item.firstName} ${item.lastName}`;
        } else if (t === 'odontologist') {
            this.entityIcon = 'solar:users-group-rounded-bold-duotone';
            this.placeholder = 'Buscar odontólogo...';
            this.displayFn = (item) => {
                const user = item.user || item;
                if (typeof user !== 'object') return String(user || '');
                return user.fullName
                    || `${user.firstName || ''} ${user.lastName || ''}`.trim()
                    || 'Sin nombre';
            };
        } else if (t === 'business') {
            this.entityIcon = 'solar:buildings-bold-duotone';
            this.placeholder = 'Buscar negocio o clínica...';
            this.displayFn = (item) => item.commercialName || item.name || 'Sin nombre';
        }

        // Debounce search input
        this.searchSubject.pipe(
            debounceTime(300),
            distinctUntilChanged()
        ).subscribe(term => {
            if (term.length === 0 || term.length >= 3) {
                this.page.set(1); // Reset page on new Search
                this.searchQ.set(term);
            }
        });
    }

    onSearchChange(term: string) {
        this.searchSubject.next(term);
    }

    clearSearch() {
        this.searchTerm = '';
        this.page.set(1);
        this.searchQ.set('');
    }

    onScroll(event: Event) {
        const el = event.target as HTMLElement;
        const threshold = 50;
        const atBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + threshold;

        if (atBottom && !this.isLoading() && this.results().length < this.total()) {
            this.page.update(p => p + 1);
        }
    }

    select(item: any) {
        this.dialogRef.close(item);
    }
}
